import { effect, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { GbrUser, GraphUser, LanguageCode, TokenClaims } from '@gbr-monorepo/interfaces';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { SetUser, SetUserLanguage } from '../state/user.action';
import { UserService } from '../../shared/services/user.service';
import { environment } from '../../../environments/environment';
import { User } from '../../shared/interfaces/User';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { toSignal } from '@angular/core/rxjs-interop';
import { GraphService } from '@gbr-monorepo/frontend';
import { AccountInfo } from '@azure/msal-common';
import { getEnumValue } from '@gbr-monorepo/backend-lib/helpers';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly userService = inject(UserService);
    private readonly graphService = inject(GraphService);
    private readonly store$ = inject(Store);
    private readonly router = inject(Router);

    private readonly msalService = inject(MsalService);
    private readonly msalBroadcastService = inject(MsalBroadcastService);

    private readonly inProgress = toSignal(this.msalBroadcastService.inProgress$);

    private readonly _isAuthenticated = signal(false);
    public readonly isAuthenticated = this._isAuthenticated.asReadonly();

    constructor() {
        effect(async () => {
            const status = this.inProgress();
            console.log(status);
            if (status === InteractionStatus.None) {
                await this.handleLogin();
            }
        });
    }

    public logout(): void {
        this.msalService.logoutRedirect();
    }

    private async handleLogin(): Promise<void> {
        const activeAccount = await this.getActiveAccount();
        const idTokenClaims = activeAccount?.idTokenClaims as TokenClaims | undefined;
        const graphUser = await this.fetchGraphUser();
        if (idTokenClaims && !!idTokenClaims['siemens_gid']) {
            if (graphUser && (this.isGermanUser(graphUser) || environment.stage !== 'prod')) {
                await this.loadGbrUserAndSetState(idTokenClaims, graphUser);
            } else {
                await this.router.navigate(['/auth/unauthorized']);
            }
        } else {
            await this.router.navigate(['/auth/unauthorized']);
        }
    }

    private async getActiveAccount(): Promise<AccountInfo | undefined> {
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length === 0) {
            return;
        }
        const activeAccount = this.msalService.instance.getActiveAccount() || accounts[0];
        if (!activeAccount) {
            return;
        }
        this.msalService.instance.setActiveAccount(activeAccount);
        return activeAccount;
    }

    private async fetchGraphUser(): Promise<Pick<GraphUser, 'country' | 'department'> | undefined> {
        return await firstValueFrom(this.graphService.getMeWithSelect(['country', 'department']));
    }

    private async loadGbrUserAndSetState(
        idTokenClaims: TokenClaims,
        graphUser: Pick<GraphUser, 'country' | 'department'>
    ): Promise<void> {
        try {
            const user = await firstValueFrom(this.userService.getMe());
            const gbrUser = this.mapUserToGbrUser(idTokenClaims, graphUser, user);
            const languageCode = getEnumValue(LanguageCode, user.language) ?? LanguageCode.EN;
            await this.dispatchStoreData(gbrUser, languageCode);
            this._isAuthenticated.set(true);
        } catch {
            await this.router.navigate(['/auth/unauthorized']);
        }
    }

    private dispatchStoreData(user: GbrUser, language: LanguageCode): Promise<unknown> {
        return firstValueFrom(
            this.store$.dispatch([
                new SetUser({
                    isAuthenticated: true,
                    user
                }),
                new SetUserLanguage({ language })
            ])
        );
    }

    private mapUserToGbrUser(
        idTokenClaims: TokenClaims,
        graphUser: Pick<GraphUser, 'country' | 'department'>,
        gbrUser: User
    ): GbrUser {
        const { department } = graphUser;
        return {
            gid: gbrUser.gid || idTokenClaims.siemens_gid,
            firstName: gbrUser.firstName || idTokenClaims.given_name,
            lastName: gbrUser.lastName || idTokenClaims.family_name,
            email: gbrUser.email || idTokenClaims.email,
            orgCode: gbrUser.orgCode || department || '',
            roles: gbrUser.roles,
            delegateForGroups: gbrUser.delegateForGroups || []
        };
    }

    private isGermanUser(graphUser: Pick<GraphUser, 'country'>): boolean {
        const { country } = graphUser;
        return !country || country?.toLowerCase() === 'germany';
    }
}
